/**
 * Compresses an image file to a specified quality and maximum dimension.
 * @param file The image file to compress.
 * @param quality The quality of the compressed image (0 to 1).
 * @param maxDimension Optional maximum dimension for the compressed image. If not provided, defaults to 1920 pixels.
 * @returns A Promise that resolves to the compressed image file.
 */
export const compressImage = async (
	file: File,
	quality: number,
	maxDimension?: number,
): Promise<File> => {
	return new Promise((resolve, reject) => {
		const canvas = document.createElement("canvas");
		const ctx = canvas.getContext("2d");
		if (!ctx) {
			reject(new Error("Failed to get 2D context from canvas."));
			return;
		}
		const img = new Image();

		img.onload = () => {
			let { width, height } = img;
			const targetMaxDimension = maxDimension || 1920;

			if (width > height && width > targetMaxDimension) {
				height = (height * targetMaxDimension) / width;
				width = targetMaxDimension;
			} else if (height > targetMaxDimension) {
				width = (width * targetMaxDimension) / height;
				height = targetMaxDimension;
			}

			canvas.width = width;
			canvas.height = height;
			ctx.drawImage(img, 0, 0, width, height);

			canvas.toBlob(
				(blob) => {
					if (!blob) {
						reject(
							new Error(
								"Image compression failed: blob is null.",
							),
						);
						return;
					}
					const compressedFile = new File([blob], file.name, {
						type: file.type,
						lastModified: Date.now(),
					});
					resolve(compressedFile);
				},
				file.type,
				quality,
			);
		};

		img.onerror = () => {
			reject(new Error("Failed to load image"));
		};

		img.src = URL.createObjectURL(file);
	});
};

/**
 * Converts a File object to a base64-encoded string.
 * @param file The file to convert.
 * @returns A Promise that resolves to the base64-encoded string.
 */
export const fileToBase64 = (file: File): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = reader.result as string;
			// Remove data:image/...;base64, prefix to get just the base64 data
			const base64Data = result.split(",")[1];
			resolve(base64Data);
		};
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
};
